// The Integrations tab's per-band debugger: connections, sync jobs, connect attempts and the rows behind each
// vendor-fed widget. Each section flags its own problems, and opens by itself when it has one. Money is out of scope.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, X } from "lucide-react";
import { Card, DataTable, EmptyState, Icon, IconButton, Modal, Picker, SplitButton, Switch, Tag, type DataTableColumn } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminBandDebug,
  useAdminBandSync,
  useAdminBands,
  useAdminSyncCapture,
  useForceAdminSync,
  useResetAdminBudget,
  type AdminBandSyncRow,
  type AdminConnectAttempt,
  type AdminDebugConnection,
  type AdminDebugIssue,
  type AdminDebugWidget,
} from "../../app/hooks/useAdmin.js";
import { pushToast } from "../../app/toasts.js";
import { ago, jobState, nextRun } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

const OWNER_LABEL: Record<string, string> = { band: "Band", us: "Us", vendor: "Vendor" };
const ownerLabel = (owner: string | null | undefined) => OWNER_LABEL[owner ?? ""] ?? owner ?? "";

const STATE_TONE: Record<string, "danger" | "warning" | "neutral" | "success"> = {
  blocked: "danger",
  paused: "neutral",
  queued: "warning",
  running: "warning",
  scheduled: "success",
};

const jobKey = (j: AdminBandSyncRow) => `${j.provider}/${j.kind}`;

/** Down when closed, up when open. The stylesheet turns it off the `<details>` state, so it cannot disagree with it. */
function SectionChevron() {
  return <Icon icon={ChevronDown} size={16} className={styles.sectionChevron} />;
}

/** A collapsible section. It opens by itself while it has something to flag, and its header says how much. */
function Section({ title, flag, tone = "danger", open, children }: {
  title: string;
  flag?: string;
  tone?: "danger" | "warning";
  open: boolean;
  children: ReactNode;
}) {
  return (
    <details className={styles.section} open={open}>
      <summary className={styles.sectionHead}>
        <SectionChevron />
        <span className={styles.sectionTitle}>{title}</span>
        {flag && <Tag tone={tone} size="sm">{flag}</Tag>}
      </summary>
      <div className={styles.sectionBody}>{children}</div>
    </details>
  );
}

/** Each issue is a banner in its chip's colour. Dismissing only hides it for now: it is still true, so it comes back on
 * the next visit rather than being forgotten. */
function Issues({ issues, onDismiss }: { issues: readonly AdminDebugIssue[]; onDismiss: (issue: AdminDebugIssue) => void }) {
  if (issues.length === 0) return null;
  return (
    <ul className={styles.issues}>
      {issues.map((i) => {
        const tone = i.severity === "error" ? "danger" : "warning";
        return (
          <li key={i.text} className={styles.issue} data-tone={tone}>
            <Tag tone={tone} size="sm">{ownerLabel(i.owner)}</Tag>
            <span className={styles.issueText}>{i.text}</span>
            <IconButton icon={X} label={`Dismiss: ${i.text}`} size="sm" onClick={() => onDismiss(i)} />
          </li>
        );
      })}
    </ul>
  );
}

const issueFlag = (issues: readonly AdminDebugIssue[]) => (issues.length ? `${issues.length} to look at` : undefined);
const issueTone = (issues: readonly AdminDebugIssue[]) => (issues.some((i) => i.severity === "error") ? "danger" : "warning");

/** A long sentence in a table cell. It takes the row's spare width and ends in an ellipsis, with the whole text on
 * hover, so it can never push the columns after it out of the card. Needs the table to carry `styles.fit`. */
function Clipped({ text, error }: { text: string; error?: boolean }) {
  return <span className={error ? `${styles.clip} ${styles.errorText}` : styles.clip} title={text}>{text}</span>;
}

const CONNECTION_COLUMNS: DataTableColumn<AdminDebugConnection>[] = [
  { key: "provider", header: "Provider", cell: (c) => <strong>{c.provider}</strong> },
  { key: "status", header: "Status", cell: (c) => <Tag tone={c.status === "error" ? "danger" : "success"} size="sm">{c.status}</Tag> },
  { key: "handle", header: "Account", cell: (c) => c.handle ?? "—" },
  { key: "since", header: "Connected", cell: (c) => ago(c.connectedOn) },
  { key: "data", header: "Last data", cell: (c) => ago(c.lastDataOn, "never") },
  { key: "error", header: "Last error", cell: (c) => <Clipped text={c.lastError ?? "—"} /> },
];

const ATTEMPT_COLUMNS: DataTableColumn<AdminConnectAttempt>[] = [
  { key: "when", header: "When", cell: (a) => ago(a.occurredOn) },
  { key: "provider", header: "Provider", cell: (a) => <strong>{a.provider}</strong> },
  {
    key: "outcome",
    header: "Outcome",
    cell: (a) => (
      <Tag tone={a.outcome === "connected" ? "success" : a.outcome === "failed" ? "danger" : "neutral"} size="sm">
        {a.outcome}
      </Tag>
    ),
  },
  { key: "reason", header: "Reason", cell: (a) => <Clipped text={a.reason ?? "—"} /> },
];

const syncColumns = (
  syncNow: (j: AdminBandSyncRow) => void,
  onResponse: (j: AdminBandSyncRow) => void,
  onResetQuota: (j: AdminBandSyncRow) => void,
  pendingKey: string | null,
): DataTableColumn<AdminBandSyncRow>[] => [
  { key: "job", header: "Job", cell: (j) => <code className={styles.code}>{`${j.provider} · ${j.kind}`}</code> },
  {
    key: "state",
    header: "State",
    cell: (j) => {
      const state = jobState(j);
      return <Tag tone={STATE_TONE[state] ?? "neutral"} size="sm">{state}</Tag>;
    },
  },
  { key: "ran", header: "Last run", cell: (j) => ago(j.lastRunOn, "never") },
  { key: "data", header: "Last data", cell: (j) => ago(j.lastLandOn, "never") },
  { key: "next", header: "Next run", cell: (j) => nextRun(j) },
  {
    key: "quota",
    header: "Quota today",
    cell: (j) =>
      j.budgetCap == null ? "—" : (
        <span className={(j.budgetUsed ?? 0) >= j.budgetCap ? styles.errorText : undefined}>
          {j.budgetUsed ?? 0} of {j.budgetCap}
        </span>
      ),
  },
  {
    key: "why",
    header: "Why",
    // One column for the reasons a row can carry, in the order they matter: a block will not fix itself, an error is
    // the last failure, a note is why the last SUCCESS fetched nothing. With none of those, the plain last status.
    cell: (j) => {
      const failures = (j.consecutiveFailures ?? 0) > 1 ? ` (${j.consecutiveFailures} in a row)` : "";
      if (j.blockedReason) return <Clipped text={`Blocked: ${j.blockedReason}`} error />;
      if (j.lastError) return <Clipped text={`${j.lastError}${failures}`} error />;
      return <Clipped text={j.lastNote ?? j.lastStatus ?? "—"} />;
    },
  },
  { key: "owner", header: "Who", cell: (j) => (j.owner ? ownerLabel(j.owner) : <span className={styles.zero}>—</span>) },
  {
    key: "action",
    header: "Action",
    align: "end",
    // A paused job and one already queued or running cannot be forced (the server refuses both), so the button is
    // absent rather than present-and-refused. The caret appears once a forced run has left a response to read.
    cell: (j) => {
      const state = jobState(j);
      const canForce = state !== "paused" && state !== "running" && state !== "queued";
      if (!canForce) return null;
      return (
        <SplitButton
          size="xs"
          variant="ghost"
          placement="above"
          loading={pendingKey === jobKey(j)}
          onClick={() => syncNow(j)}
          menuLabel={`More for ${j.provider} ${j.kind}`}
          actions={[
            { label: "View last response", onSelect: () => onResponse(j), disabled: !j.capturedOn },
            { label: "Reset daily quota", onSelect: () => onResetQuota(j), disabled: (j.budgetUsed ?? 0) === 0 },
          ]}
        >
          Sync now<span className={styles.srOnly}> {j.provider} {j.kind}</span>
        </SplitButton>
      );
    },
  },
];

/** What every vendor call answered on one job's last forced run. */
function ResponseDialog({ bandId, job, onClose }: { bandId: string; job: AdminBandSyncRow; onClose: () => void }) {
  const capture = useAdminSyncCapture(bandId, job.provider ?? "", job.kind ?? "");
  const c = capture.data;
  return (
    <Modal title={`${job.provider} · ${job.kind}`} subtitle="Last forced run" size="lg" onClose={onClose}>
      {capture.isLoading ? (
        <p className={styles.hint}>Reading…</p>
      ) : capture.isError ? (
        <p className={styles.error}>{errorMessage(capture.error, "Could not read this run's response.")}</p>
      ) : (
        <div className={styles.stack}>
          <p>
            {ago(c?.capturedOn)} · {c?.outcome}
            {c?.error && ` · ${c.error}`}
            {c?.note && ` · ${c.note}`}
          </p>
          {(c?.calls?.length ?? 0) === 0 && <p className={styles.hint}>This run made no vendor calls.</p>}
          {c?.calls?.map((call, i) => (
            <details key={`${i}-${call.url}`} className={styles.subsection}>
              <summary className={styles.sectionHead}>
                <SectionChevron />
                <Tag tone={(call.status ?? 0) >= 400 || call.status == null ? "danger" : "success"} size="sm">
                  {call.status ?? call.outcome}
                </Tag>
                <code className={styles.code}>{`${call.method} ${call.url}`}</code>
                <span className={styles.zero}>{call.ms} ms</span>
              </summary>
              <div className={styles.sectionBody}>
                {call.truncated && <p className={styles.hint}>Cut short: this answer was too large to keep whole.</p>}
                <pre className={styles.raw}>{call.body ?? "No response body."}</pre>
              </div>
            </details>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** The rows behind one widget. Plain on purpose: this shows what is there, not how the widget draws it. */
function WidgetRow({ widget }: { widget: AdminDebugWidget }) {
  const [raw, setRaw] = useState(false);
  const columns = (widget.columns ?? []).map((name, index): DataTableColumn<readonly (string | null)[]> => ({
    key: name,
    header: name,
    cell: (row) => {
      const value = row[index];
      if (value == null) return <span className={styles.zero}>—</span>;
      return <span className={styles.widgetCell} title={value}>{value}</span>;
    },
  }));
  return (
    <details className={styles.subsection}>
      <summary className={styles.sectionHead}>
        <SectionChevron />
        <strong>{widget.label}</strong>
        <span className={styles.zero}>{widget.page}</span>
        {widget.emptyReason ? (
          <Tag tone="neutral" size="sm">Empty: {widget.emptyReason}</Tag>
        ) : (
          <span>{widget.rowCount} {widget.rowCount === 1 ? "row" : "rows"}</span>
        )}
        <span className={styles.zero}>{ago(widget.asOf)} · {widget.source}</span>
      </summary>
      <div className={styles.sectionBody}>
        {widget.emptyReason ? (
          <p className={styles.hint}>{widget.emptyReason}</p>
        ) : (
          <>
            <span className={`${styles.toggle} ${styles.hint}`}>
              <Switch checked={raw} onChange={setRaw} label={`Show ${widget.label} as JSON`} size="sm" />
              <span aria-hidden="true">JSON</span>
            </span>
            {raw ? (
              <pre className={styles.raw}>{widget.raw}</pre>
            ) : (
              <DataTable columns={columns} rows={(widget.rows ?? []) as (readonly (string | null)[])[]}
                rowKey={(_, i) => String(i)} maxBlockSize="28rem" />
            )}
          </>
        )}
      </div>
    </details>
  );
}

/** Says when a run a person started has ended, since the row alone changes quietly. */
function useFinishedToasts(jobs: readonly AdminBandSyncRow[] | null | undefined, onFinished: () => void) {
  const active = useRef(new Set<string>());
  useEffect(() => {
    if (!jobs) return;
    for (const j of jobs) {
      const key = jobKey(j);
      const state = jobState(j);
      if (state === "queued" || state === "running") {
        active.current.add(key);
      } else if (active.current.delete(key)) {
        pushToast(`${j.provider} · ${j.kind} finished: ${j.lastError ?? j.lastStatus ?? "done"}`, j.lastError ? undefined : "success");
        onFinished();
      }
    }
  }, [jobs, onFinished]);
}

function SyncSection({ bandId }: { bandId: string }) {
  const sync = useAdminBandSync(bandId);
  const force = useForceAdminSync(bandId);
  const resetBudget = useResetAdminBudget(bandId);
  const qc = useQueryClient();
  const [reland, setReland] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [response, setResponse] = useState<AdminBandSyncRow | null>(null);
  const jobs = sync.data?.jobs;
  // A finished run may have landed new data, so the widget tables read again.
  const refreshDebug = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["admin", "band-debug", bandId] });
  }, [qc, bandId]);
  useFinishedToasts(jobs, refreshDebug);

  const syncNow = (j: AdminBandSyncRow) => {
    if (!j.provider || !j.kind) return;
    setPendingKey(jobKey(j));
    force.mutate(
      { provider: j.provider, kind: j.kind, reland },
      {
        onError: (e) => pushToast(errorMessage(e, `Could not queue ${j.provider}.`)),
        onSettled: () => setPendingKey(null),
      },
    );
  };

  const resetQuota = (j: AdminBandSyncRow) => {
    if (!j.provider) return;
    resetBudget.mutate(j.provider, {
      onSuccess: () => pushToast(`${j.provider}'s daily quota is reset. Press Sync now to run it.`, "success"),
      onError: (e) => pushToast(errorMessage(e, `Could not reset ${j.provider}'s quota.`)),
    });
  };

  const flagged = jobs?.filter((j) => j.issue).length ?? 0;
  const running = jobs?.filter((j) => ["queued", "running"].includes(jobState(j))).length ?? 0;
  const flag = flagged ? `${flagged} to look at` : running ? `${running} running` : undefined;

  return (
    <Section title="Sync jobs" flag={flag} tone={flagged ? "danger" : "warning"} open={flagged > 0 || running > 0}>
      <p className={styles.hint}>Sync now skips the release window and the minimum interval, never the daily budget.</p>
      <span className={`${styles.toggle} ${styles.hint}`}>
        <Switch checked={reland} onChange={setReland} label="Re-land even if the answer is unchanged" size="sm" />
        <span aria-hidden="true">Re-land even if unchanged</span>
      </span>
      {sync.isLoading ? (
        <p className={styles.hint}>Reading…</p>
      ) : sync.isError ? (
        <p className={styles.error}>{errorMessage(sync.error, "Could not read this band's sync jobs.")}</p>
      ) : (jobs?.length ?? 0) === 0 ? (
        <EmptyState title="No sync jobs" description="This band has connected nothing that syncs." />
      ) : (
        <DataTable className={styles.fit} columns={syncColumns(syncNow, setResponse, resetQuota, pendingKey)} rows={jobs ?? []}
          rowKey={jobKey} />
      )}
      {response && <ResponseDialog bandId={bandId} job={response} onClose={() => setResponse(null)} />}
    </Section>
  );
}

function DebugSections({ bandId }: { bandId: string }) {
  const debug = useAdminBandDebug(bandId);
  const d = debug.data;
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  const dismiss = (issue: AdminDebugIssue) => setDismissed((seen) => [...seen, issue.text ?? ""]);
  const showing = (issues: readonly AdminDebugIssue[] | null | undefined) =>
    (issues ?? []).filter((i) => !dismissed.includes(i.text ?? ""));
  const connectionIssues = showing(d?.connectionIssues);
  const attemptIssues = showing(d?.attemptIssues);
  const widgetIssues = showing(d?.widgetIssues);
  return (
    <>
      {/* The sync jobs have their own read, so a failure here must not take them with it. */}
      {debug.isLoading && <p className={styles.hint}>Reading…</p>}
      {debug.isError && <p className={styles.error}>{errorMessage(debug.error, "Could not read this band's integrations.")}</p>}
      {d && <Section title="Connections" flag={issueFlag(connectionIssues)} tone={issueTone(connectionIssues)} open={connectionIssues.length > 0}>
        <Issues issues={connectionIssues} onDismiss={dismiss} />
        {(d?.connections?.length ?? 0) === 0 ? (
          <p className={styles.hint}>Nothing connected.</p>
        ) : (
          <DataTable className={styles.fit} columns={CONNECTION_COLUMNS} rows={d?.connections ?? []} rowKey={(c) => c.provider ?? ""} />
        )}
      </Section>}
      <SyncSection bandId={bandId} />
      {d && <Section title="Connect attempts" flag={issueFlag(attemptIssues)} tone={issueTone(attemptIssues)} open={attemptIssues.length > 0}>
        <Issues issues={attemptIssues} onDismiss={dismiss} />
        {(d?.attempts?.length ?? 0) === 0 ? (
          <p className={styles.hint}>No connects recorded yet.</p>
        ) : (
          <DataTable className={styles.fit} columns={ATTEMPT_COLUMNS} rows={d?.attempts ?? []} rowKey={(a, i) => `${a.occurredOn}-${i}`} />
        )}
      </Section>}
      {d && <Section title="Widget data" flag={issueFlag(widgetIssues)} tone={issueTone(widgetIssues)} open={widgetIssues.length > 0}>
        <Issues issues={widgetIssues} onDismiss={dismiss} />
        {d?.widgets?.map((w) => <WidgetRow key={w.key} widget={w} />)}
      </Section>}
    </>
  );
}

/** One band's integrations. The band is chosen here or by clicking one under "Needs attention". */
export function BandDebugger({ bandId, onBand }: { bandId: string | undefined; onBand: (bandId: string | undefined) => void }) {
  const bands = useAdminBands();
  const options = (bands.data ?? [])
    .map((b) => ({ value: b.bandId ?? "", label: b.isFixture ? `${b.name} (fixture)` : (b.name ?? "") }))
    .filter((o) => o.value)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Card title="Integration debugger" subtitle="One band's connections, sync jobs, connect attempts and the data behind each widget.">
      <div className={styles.stack}>
        <div className={styles.controls}>
          <div className={styles.search}>
            <Picker label="Band" hideLabel typeahead placeholder="Choose a band…" value={bandId ?? ""}
              onChange={(v) => onBand(v || undefined)} options={options} />
          </div>
        </div>
        {/* Its own block: .stack's gap would land under each divider and push every title off centre. */}
        {bandId && <div><DebugSections key={bandId} bandId={bandId} /></div>}
      </div>
    </Card>
  );
}
